import { Sparkles } from "lucide-react";
import type { FC } from "react";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import { InspirationTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { InspirationSliderSheet } from "web_myplann/Inspiration/Components/InspirationSliderSheet";
import { EarlyFreedomColdIntro } from "web_myplann/Inspiration/EarlyFreedom/Components/EarlyFreedomColdIntro";
import { EarlyFreedomResult } from "web_myplann/Inspiration/EarlyFreedom/Components/EarlyFreedomResult";
import {
	EARLY_FREEDOM_GOAL_CATEGORY,
	EARLY_FREEDOM_IMAGE,
	EARLY_FREEDOM_RENT_MAX,
	EARLY_FREEDOM_RENT_MIN,
	EARLY_FREEDOM_RENT_STEP,
	EARLY_FREEDOM_TARGET_FALLBACK,
	EARLY_FREEDOM_TARGET_MAX,
	EARLY_FREEDOM_TARGET_MIN,
	EARLY_FREEDOM_TARGET_STEP,
} from "web_myplann/Inspiration/EarlyFreedom/constants";
import { useEarlyFreedomGoalRecap } from "web_myplann/Inspiration/EarlyFreedom/Hooks/useEarlyFreedomGoalRecap";
import { useEarlyFreedomJourney } from "web_myplann/Inspiration/EarlyFreedom/Hooks/useEarlyFreedomJourney";
import { mapEarlyFreedomGoalPreset } from "web_myplann/Inspiration/EarlyFreedom/Mappers/mapEarlyFreedomGoalPreset";
import { useInspirationCtas } from "web_myplann/Inspiration/Hooks/useInspirationCtas";
import { useOpenGoalFormFromInspiration } from "web_myplann/Inspiration/Hooks/useOpenGoalFormFromInspiration";
import { useContactAdvisor } from "web_myplann/Shared/Hooks/useContactAdvisor";

const PageResources = Resources.Inspiration.EarlyFreedom;

export const EarlyFreedomInspirationPage: FC = () => {
	const { t } = useResource();
	const { openGoalForm } = useOpenGoalFormFromInspiration();
	const { openChat } = useContactAdvisor();
	const {
		context,
		activeSheet,
		openSheet,
		closeSheet,
		patchInputs,
		submitInputs,
	} = useEarlyFreedomJourney();
	const recap = useEarlyFreedomGoalRecap(context);

	const isCold = context.level === "cold";

	const contactAdvisor = () => openChat(t(PageResources.advisorMessage));

	const createGoal = () => {
		const preset = mapEarlyFreedomGoalPreset(context);
		openGoalForm(
			{
				category: EARLY_FREEDOM_GOAL_CATEGORY,
				name: t(PageResources.title),
				monthlyRent: preset.monthlyRent,
				drawdownMode: RentDrawdownMode.Perpetual,
				dueDate: preset.dueDate,
			},
			InspirationTypeEnum.EarlyFreedom,
		);
	};

	const { goalRecap, primaryCta, secondaryCta } = useInspirationCtas({
		isCold,
		coldCta: {
			label: t(PageResources.cta),
			icon: Sparkles,
			onClick: () => openSheet("quick"),
		},
		goalRecap: recap,
		createGoal: { label: t(PageResources.createGoal), onClick: createGoal },
		advisor: {
			label: t(PageResources.discussAdvisor),
			message: t(PageResources.advisorMessage),
		},
	});

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={EARLY_FREEDOM_IMAGE}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				goalRecap={goalRecap}
				primaryCta={primaryCta}
				secondaryCta={secondaryCta}
			>
				{isCold ? (
					<EarlyFreedomColdIntro />
				) : (
					<EarlyFreedomResult
						context={context}
						onChangeTarget={(targetExpenses) => patchInputs({ targetExpenses })}
						onChangeSaving={(monthlySaving) => patchInputs({ monthlySaving })}
						onSelectScenario={(scenario) => patchInputs({ scenario })}
						onEditIncome={() => openSheet("income")}
						onContact={contactAdvisor}
					/>
				)}
			</InspirationDetailLayout>

			<InspirationSliderSheet
				open={activeSheet === "quick"}
				copy={PageResources.QuickSheet}
				hint={PageResources.QuickSheet.hint}
				initial={EARLY_FREEDOM_TARGET_FALLBACK}
				min={EARLY_FREEDOM_TARGET_MIN}
				max={EARLY_FREEDOM_TARGET_MAX}
				step={EARLY_FREEDOM_TARGET_STEP}
				preventScrollOnClose
				onClose={closeSheet}
				onSubmit={(targetExpenses) => submitInputs({ targetExpenses })}
			/>

			<InspirationSliderSheet
				open={activeSheet === "income"}
				copy={PageResources.IncomeSheet}
				hint={PageResources.IncomeSheet.hint}
				initial={context.rentNet}
				min={EARLY_FREEDOM_RENT_MIN}
				max={EARLY_FREEDOM_RENT_MAX}
				step={EARLY_FREEDOM_RENT_STEP}
				onClose={closeSheet}
				onSubmit={(rentNet) => submitInputs({ rentNet })}
			/>
		</>
	);
};
